// POST /api/transactions/links -- confirms a link candidate as a transfer
// or cc_payment (M1 plan §5.1 point 6). The reject path
// (`POST .../links/{group_id}/reject`) returns 501 in M1 -- there is
// deliberately no `useRejectLink` hook here; `TransferReviewCard` renders
// "Not a transfer" disabled with an honest reason instead of wiring a
// no-op (execution plan §9.8).
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import client from '../client';
import { parseEnvelope } from '../envelope';

const uuidSchema = z.string().uuid();
const candidateIdSchema = z.string().min(1);
const emptyMetaSchema = z.object({}).strict();
export const confirmLinkResponseSchema = z.object({
  group_id: uuidSchema,
  legs: z.array(uuidSchema),
}).strict();

export type ConfirmLinkResponse = z.infer<typeof confirmLinkResponseSchema>;

export interface ConfirmLinkInput {
  candidateId: string;
  kind: 'transfer' | 'cc_payment';
}

export function useConfirmLink() {
  const queryClient = useQueryClient();
  return useMutation({
    retry: false,
    mutationFn: async ({ candidateId, kind }: ConfirmLinkInput) => {
      candidateIdSchema.parse(candidateId);
      const response = await client.post('/api/transactions/links', {
        candidate_id: candidateId,
        kind,
      });
      const parsed = parseEnvelope(confirmLinkResponseSchema, response.data);
      emptyMetaSchema.parse(parsed.meta);
      return parsed.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['transactions'] });
    },
  });
}
